import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Tooltip,
  TextField,
  Container,
  Typography,
  Modal,
  useTheme,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditCourse = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { CourseId } = useParams();
  
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    course_name: '',
    course_duration: '',
    course_description: '',
    basic_course_fee: '',
    gst_percentage: '0',
    organization: '',
    branch: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://wims.revisetoexcel.com';

  // Get authentication token
  const getAuthToken = () => {
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      sessionStorage.getItem('token') ||
      sessionStorage.getItem('authToken') ||
      sessionStorage.getItem('access_token') ||
      sessionStorage.getItem('accessToken')
    );
  };

  // Get auth headers
  const getAuthHeaders = () => {
    const token = getAuthToken();
    
    if (!token) {
      console.warn('No authentication token found');
      return {
        'Content-Type': 'application/json',
      };
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  // Fetch course data on component mount
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        const token = getAuthToken();

        if (!token) {
          setSnackbarMessage('Authentication required. Please log in.');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
          setLoading(false);
          navigate(-1);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/course/courses/${CourseId}/`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });

        if (response.status === 401) {
          setSnackbarMessage('Session expired. Please log in again.');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          setLoading(false);
          navigate(-1);
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Populate form with fetched data
        setFormData({
          course_name: data.course_name || '',
          course_duration: data.course_duration || '',
          course_description: data.course_description || '',
          basic_course_fee: data.basic_course_fee || '',
          gst_percentage: '0',
          organization: data.organization || '',
          branch: data.branch || '',
        });
      } catch (error) {
        console.error('Error fetching course data:', error);
        setSnackbarMessage('Failed to load course data. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setTimeout(() => navigate(-1), 2000);
      } finally {
        setLoading(false);
      }
    };

    if (CourseId) {
      fetchCourseData();
    } else {
      setSnackbarMessage('Invalid course ID.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      setTimeout(() => navigate(-1), 2000);
    }
  }, [CourseId]);

  // Fetch organizations and branches
  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Auto-select organization and branch after organizations are loaded
  useEffect(() => {
    if (organizations.length === 1 && !formData.organization) {
      setFormData(prev => ({
        ...prev,
        organization: organizations[0].id
      }));
    }
  }, [organizations, formData.organization]);

  // Filter branches based on selected organization
  useEffect(() => {
    if (formData.organization && allBranches.length > 0) {
      // Filter branches that belong to the selected organization
      const filteredBranches = allBranches.filter(
        (branch) => branch.organization === parseInt(formData.organization)
      );
      setBranches(filteredBranches);

      // Check if current branch belongs to the selected organization
      if (formData.branch) {
        const branchStillValid = filteredBranches.some(
          (branch) => branch.id === parseInt(formData.branch)
        );
        
        // If current branch is not valid for this organization, reset it
        if (!branchStillValid) {
          // Auto-select first branch if available
          if (filteredBranches.length > 0) {
            setFormData(prev => ({
              ...prev,
              branch: filteredBranches[0].id
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              branch: ''
            }));
          }
        }
      } else if (filteredBranches.length === 1 && !formData.branch) {
        // Auto-select branch if only one is available
        setFormData(prev => ({
          ...prev,
          branch: filteredBranches[0].id
        }));
      }
    } else if (!formData.organization) {
      // No organization selected, clear branches and branch
      setBranches([]);
      if (formData.branch) {
        setFormData(prev => ({
          ...prev,
          branch: ''
        }));
      }
    }
  }, [formData.organization, allBranches]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.course_name.trim()) {
      errors.course_name = 'Course name is required';
    }
    
    if (!formData.course_duration.trim()) {
      errors.course_duration = 'Course duration is required';
    }
    
    if (!formData.course_description.trim()) {
      errors.course_description = 'Course description is required';
    }
    
    if (!formData.basic_course_fee || parseFloat(formData.basic_course_fee) <= 0) {
      errors.basic_course_fee = 'Valid course fee is required';
    }
    
    if (!formData.organization) {
      errors.organization = 'Organization is required';
    }
    
    if (!formData.branch) {
      errors.branch = 'Branch is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save/submit
  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSnackbarMessage('Please fill all required fields correctly.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      setSaving(true);
      const token = getAuthToken();

      if (!token) {
        setSnackbarMessage('Authentication required. Please log in.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setSaving(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/course/courses/${CourseId}/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          course_name: formData.course_name.trim(),
          course_duration: formData.course_duration.trim(),
          course_description: formData.course_description.trim(),
          basic_course_fee: parseFloat(formData.basic_course_fee).toFixed(2),
          gst_percentage: parseFloat(formData.gst_percentage).toFixed(2),
          organization: parseInt(formData.organization),
          branch: parseInt(formData.branch)
        }),
      });

      if (response.status === 401) {
        setSnackbarMessage('Session expired. Please log in again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        setSaving(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const updatedCourse = await response.json();

      setSnackbarMessage(`Course "${updatedCourse.course_name}" updated successfully!`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error updating course:', error);
      setSnackbarMessage(error.message || 'Failed to update course. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setSaving(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const orgResponse = await fetch(`${API_BASE_URL}/api/master/organizations/`, {
        headers: getAuthHeaders(),
      });

      const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      const branchResponse = await fetch(
        `${API_BASE_URL}/api/master/organization/${encodeURIComponent(sessionUser.organization_id || '')}/branches/`,
        {
        headers: getAuthHeaders(),
        }
      );

      if (!orgResponse.ok || !branchResponse.ok) {
        throw new Error("Failed to fetch data");
      }

      const orgData = await orgResponse.json();
      const branchData = await branchResponse.json();

      setOrganizations(orgData);
      setAllBranches(branchData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const isFormValid = () => {
    return (
      formData.course_name.trim() &&
      formData.course_duration.trim() &&
      formData.course_description.trim() &&
      formData.basic_course_fee &&
      parseFloat(formData.basic_course_fee) > 0 &&
      formData.organization &&
      formData.branch
    );
  };

  // Show loading state while fetching data
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Box
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Edit Course Details
          </Typography>
        </Box>

        <form onSubmit={handleSave}>
          <TextField
            label="Course Name"
            name="course_name"
            variant="outlined"
            fullWidth
            value={formData.course_name}
            onChange={handleInputChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            error={!!formErrors.course_name}
            helperText={formErrors.course_name}
            required
            disabled={saving}
          />

          <TextField
            label="Course Duration"
            name="course_duration"
            variant="outlined"
            fullWidth
            value={formData.course_duration}
            onChange={handleInputChange}
            placeholder="e.g., 8 Months"
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            error={!!formErrors.course_duration}
            helperText={formErrors.course_duration}
            required
            disabled={saving}
          />

          <TextField
            label="Course Description"
            name="course_description"
            variant="outlined"
            fullWidth
            value={formData.course_description}
            onChange={handleInputChange}
            multiline
            rows={4}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            error={!!formErrors.course_description}
            helperText={formErrors.course_description}
            required
            disabled={saving}
          />

          <TextField
            label="Basic Course Fee"
            name="basic_course_fee"
            variant="outlined"
            fullWidth
            value={formData.basic_course_fee}
            onChange={handleInputChange}
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">
                <span style={{ color: theme.palette.text.primary }}>₹</span>
              </InputAdornment>,
            }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            error={!!formErrors.basic_course_fee}
            helperText={formErrors.basic_course_fee}
            required
            disabled={saving}
          />

          {/* Organization Field - Auto select and disable when only one option */}
          <FormControl  disabled fullWidth sx={{ mb: 3 }} required>
            <InputLabel
              sx={{
                color: theme.palette.text.primary,
                '&.Mui-focused': { color: theme.palette.primary.main }
              }}
            >
              Organization *
            </InputLabel>
            <Select disabled
              name="organization"
              value={formData.organization}
              label="Organization *"
              onChange={handleInputChange}
              error={!!formErrors.organization}
              sx={{
                '&.Mui-disabled': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.action.disabled,
                  },
                },
              }}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
            {organizations.length === 1 && (
              <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.info.main }}>
                Only one organization available, auto-selected
              </Typography>
            )}
            {formErrors.organization && (
              <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.error.main }}>
                {formErrors.organization}
              </Typography>
            )}
          </FormControl>

          {/* Branch Field - Shows only branches for selected organization */}
          <FormControl disabled fullWidth sx={{ mb: 3 }} required>
            <InputLabel
              sx={{
                color: theme.palette.text.primary,
                '&.Mui-focused': { color: theme.palette.primary.main }
              }}
            >
              Branch *
            </InputLabel>
            <Select
              name="branch"
              value={formData.branch}
              label="Branch *"
              onChange={handleInputChange}
              disabled
              error={!!formErrors.branch}
              sx={{
                '&.Mui-disabled': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.action.disabled,
                  },
                },
              }}
            >
              {branches.length > 0 ? (
                branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No branches available for selected organization
                </MenuItem>
              )}
            </Select>
            {!formData.organization && (
              <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.warning.main }}>
                Please select an organization first
              </Typography>
            )}
            {formData.organization && branches.length === 0 && (
              <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.warning.main }}>
                No branches found for this organization
              </Typography>
            )}
            {branches.length === 1 && formData.organization && (
              <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.info.main }}>
                Only one branch available for this organization, auto-selected
              </Typography>
            )}
            {formErrors.branch && (
              <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.error.main }}>
                {formErrors.branch}
              </Typography>
            )}
          </FormControl>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Tooltip
              title={
                !isFormValid()
                  ? "Please fill all required fields correctly"
                  : ""
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!isFormValid() || saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {saving ? 'UPDATING...' : 'UPDATE'}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
              disabled={saving}
            >
              CANCEL
            </Button>
          </Box>
        </form>

        {/* Success Modal */}
        <Modal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
              <Typography variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
                Course Updated ✅
              </Typography>
            </Box>
            <Box sx={{ padding: '20px' }}>
              <Typography sx={{ mb: 2 }}>
                Course details have been updated successfully.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(-1);
                  }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditCourse;